import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import AppLogo from '../../components/AppLogo';
import { useAuth } from '../../components/context/AuthContext';

// Theme definitions with hardcoded colors for preview cards
// (previews are isolated from the active theme so we inline the styles)
const THEMES = [
  {
    id: 'default',
    name: 'Blossom',
    description: 'Soft & Playful',
    primary: '#E91E63',
    secondary: '#8E24AA',
    accent: '#FF6B9D',
    background: '#FFF8F5',
    card: '#FFFFFF',
    foreground: '#2D1B2E',
    muted: '#F3E5F5',
    border: 'rgba(233,30,99,0.15)',
    tag: 'Default',
  },
  {
    id: 'dark',
    name: 'Dark Blossom',
    description: 'Moody & Elegant',
    primary: '#F06292',
    secondary: '#BA68C8',
    accent: '#FF8AB8',
    background: '#1A0F1A',
    card: '#2A1F2A',
    foreground: 'rgba(255,255,255,0.88)',
    muted: '#3A2F3A',
    border: 'rgba(240,98,146,0.2)',
    tag: 'Dark',
  },
  {
    id: 'theme-noir-luxe',
    name: 'Noir Luxe',
    description: 'Elite & Exclusive',
    primary: '#D4AF37',
    secondary: '#B8960C',
    accent: '#F0D060',
    background: '#0D0D0D',
    card: '#1A1A1A',
    foreground: '#F5F0E8',
    muted: '#2A2A2A',
    border: 'rgba(212,175,55,0.25)',
    tag: 'Luxury',
  },
  {
    id: 'theme-ivory-atelier',
    name: 'Ivory Atelier',
    description: 'Parisian Fashion',
    primary: '#9C7B4A',
    secondary: '#C4A882',
    accent: '#D4B896',
    background: '#FAF7F2',
    card: '#FFFFFF',
    foreground: '#2C1F14',
    muted: '#F0EAE0',
    border: 'rgba(156,123,74,0.2)',
    tag: 'Fashion',
  },
  {
    id: 'theme-royal-velvet',
    name: 'Royal Velvet',
    description: 'Heritage & Old Money',
    primary: '#7B2D3E',
    secondary: '#4A1628',
    accent: '#C4748A',
    background: '#FDF8F5',
    card: '#FFFFFF',
    foreground: '#2A0F18',
    muted: '#F5E8EC',
    border: 'rgba(123,45,62,0.15)',
    tag: 'Heritage',
  },
  {
    id: 'theme-obsidian',
    name: 'Obsidian',
    description: 'Tech Luxury',
    primary: '#00C8E8',
    secondary: '#0090B8',
    accent: '#40D8F0',
    background: '#080C10',
    card: '#101820',
    foreground: '#E8F4F8',
    muted: '#182030',
    border: 'rgba(0,200,232,0.2)',
    tag: 'Tech',
  },
  {
    id: 'theme-sage-botanica',
    name: 'Sage Botanica',
    description: 'Organic & Calm',
    primary: '#5B7A52',
    secondary: '#8FAF7A',
    accent: '#A8C89A',
    background: '#F5F8F2',
    card: '#FFFFFF',
    foreground: '#1E2E1A',
    muted: '#E8F0E4',
    border: 'rgba(91,122,82,0.2)',
    tag: 'Wellness',
  },
  {
    id: 'theme-midnight-azure',
    name: 'Midnight Azure',
    description: 'Corporate Luxury',
    primary: '#1E4D8C',
    secondary: '#2E6CB8',
    accent: '#5E9FE0',
    background: '#F8FAFD',
    card: '#FFFFFF',
    foreground: '#0A1628',
    muted: '#E8EFF8',
    border: 'rgba(30,77,140,0.15)',
    tag: 'Corporate',
  },
  {
    id: 'theme-ember',
    name: 'Ember',
    description: 'Artisan & Warm',
    primary: '#C4622D',
    secondary: '#8B4513',
    accent: '#E8956D',
    background: '#FDF8F4',
    card: '#FFFFFF',
    foreground: '#2C1208',
    muted: '#F5E8DE',
    border: 'rgba(196,98,45,0.2)',
    tag: 'Artisan',
  },
  {
    id: 'theme-celestial',
    name: 'Celestial',
    description: 'Mystical & Premium',
    primary: '#9C6FD4',
    secondary: '#6B3FA0',
    accent: '#C4A8F0',
    background: '#0F0A1E',
    card: '#1A1430',
    foreground: '#F0ECFF',
    muted: '#251C40',
    border: 'rgba(156,111,212,0.25)',
    tag: 'Mystical',
  },
  {
    id: 'theme-arctic-blanc',
    name: 'Arctic Blanc',
    description: 'Scandinavian Minimal',
    primary: '#2C6E9E',
    secondary: '#4A8CB8',
    accent: '#7AB8D8',
    background: '#F8FAFC',
    card: '#FFFFFF',
    foreground: '#0A1828',
    muted: '#EEF4F8',
    border: 'rgba(44,110,158,0.12)',
    tag: 'Minimal',
  },
  {
    id: 'theme-cherry-blossom',
    name: 'Cherry Blossom',
    description: 'Japanese Zen Luxury',
    primary: '#C8687E',
    secondary: '#7A9058',
    accent: '#F0B4C4',
    background: '#FDF8FA',
    card: '#FFFFFF',
    foreground: '#2A1520',
    muted: '#F5ECF0',
    border: 'rgba(200,104,126,0.18)',
    tag: 'Zen',
  },
];

// Mini dummy UI preview rendered entirely with inline styles
// so it's isolated from whatever theme is currently active
const ThemePreview = ({ theme }) => (
  <div
    style={{
      background: theme.background,
      borderRadius: 10,
      overflow: 'hidden',
      width: '100%',
      aspectRatio: '1 / 1',
      padding: 10,
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      border: `1px solid ${theme.border}`,
    }}
  >
    {/* Mini nav bar */}
    <div style={{
      background: theme.card,
      borderRadius: 6,
      padding: '5px 8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      border: `1px solid ${theme.border}`,
    }}>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: theme.primary }} />
        <div style={{ width: 24, height: 4, borderRadius: 2, background: theme.foreground, opacity: 0.5 }} />
      </div>
      <div style={{ display: 'flex', gap: 3 }}>
        <div style={{ width: 14, height: 4, borderRadius: 2, background: theme.muted }} />
        <div style={{ width: 14, height: 4, borderRadius: 2, background: theme.muted }} />
      </div>
    </div>

    {/* Mini cards row */}
    <div style={{ display: 'flex', gap: 5, flex: 1 }}>
      <div style={{
        flex: 1,
        background: theme.card,
        borderRadius: 6,
        padding: 6,
        border: `1px solid ${theme.border}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}>
        <div style={{ width: '60%', height: 4, borderRadius: 2, background: theme.primary }} />
        <div style={{ width: '100%', height: 3, borderRadius: 2, background: theme.muted }} />
        <div style={{ width: '80%', height: 3, borderRadius: 2, background: theme.muted }} />
        <div style={{ marginTop: 'auto', width: '50%', height: 12, borderRadius: 4, background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})` }} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, width: '38%' }}>
        <div style={{
          flex: 1,
          background: theme.card,
          borderRadius: 6,
          border: `1px solid ${theme.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{ width: 12, height: 12, borderRadius: '50%', background: `linear-gradient(135deg, ${theme.primary}, ${theme.accent})` }} />
        </div>
        <div style={{
          flex: 1,
          background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})`,
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{ width: '70%', height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.7)' }} />
        </div>
      </div>
    </div>

    {/* Mini bottom bar */}
    <div style={{ display: 'flex', gap: 4 }}>
      {[theme.primary, theme.secondary, theme.accent, theme.muted].map((color, i) => (
        <div key={i} style={{ flex: 1, height: 5, borderRadius: 3, background: color }} />
      ))}
    </div>
  </div>
);

const BENEFIT_SLIDES = [
  {
    tag: 'Your storefront',
    icon: 'Globe',
    headline: 'Give your business\nits own space on the web',
    body: 'Share one link. Clients can browse what you offer, book appointments, and order products — all from their phone.',
  },
  {
    tag: 'Appointments',
    icon: 'CalendarCheck',
    headline: 'Take bookings\naround the clock',
    body: 'Clients book themselves into your schedule 24/7. You confirm, reschedule, or update from your dashboard',
  },
  {
    tag: 'Orders',
    icon: 'ShoppingBag',
    headline: 'Sell products\nalongside your services',
    body: 'List your products once and take orders directly from your storefront. Manage everything from one organized workspace.',
  },
  {
    tag: 'Dashboard',
    icon: 'LayoutDashboard',
    headline: 'Run your whole\nbusiness from one place',
    body: 'Bookings, orders, and client details — all in one clean view. No spreadsheets, no sticky notes, no switching between apps.',
  },
];

const SHOW_MS  = 4800;
const FLIP_MS  = 380;

const BenefitCard = () => {
  const [displayedIdx, setDisplayedIdx] = useState(0);
  const [flipPhase, setFlipPhase]       = useState('idle'); // 'idle' | 'out' | 'in'
  const [isPaused,  setIsPaused]        = useState(false);
  const idxRef       = useRef(0); // mirrors displayedIdx, readable inside effects
  const targetIdxRef = useRef(null); // null = auto-advance to next; number = manual target

  // idle → out  (wait for reading time, then start flip-out)
  useEffect(() => {
    if (flipPhase !== 'idle' || isPaused) return;
    const t = setTimeout(() => setFlipPhase('out'), SHOW_MS);
    return () => clearTimeout(t);
  }, [flipPhase, displayedIdx, isPaused]);

  // out → in  (flip-out done → swap content, start flip-in)
  useEffect(() => {
    if (flipPhase !== 'out') return;
    const t = setTimeout(() => {
      const next = targetIdxRef.current !== null
        ? targetIdxRef.current
        : (idxRef.current + 1) % BENEFIT_SLIDES.length;
      targetIdxRef.current = null;
      idxRef.current = next;
      setDisplayedIdx(next);
      setFlipPhase('in');
    }, FLIP_MS);
    return () => clearTimeout(t);
  }, [flipPhase]);

  // in → idle  (flip-in done → settle)
  useEffect(() => {
    if (flipPhase !== 'in') return;
    const t = setTimeout(() => setFlipPhase('idle'), FLIP_MS);
    return () => clearTimeout(t);
  }, [flipPhase]);

  const goTo = (idx) => {
    if (flipPhase !== 'idle' || idx === idxRef.current) return;
    targetIdxRef.current = idx;
    setFlipPhase('out');
  };

  const goPrev = () => goTo((idxRef.current - 1 + BENEFIT_SLIDES.length) % BENEFIT_SLIDES.length);
  const goNext = () => goTo((idxRef.current + 1) % BENEFIT_SLIDES.length);

  const slide = BENEFIT_SLIDES[displayedIdx];

  const flipAnim =
    flipPhase === 'out' ? `clawFlipOut ${FLIP_MS}ms cubic-bezier(0.4,0,1,1) forwards`
  : flipPhase === 'in'  ? `clawFlipIn  ${FLIP_MS}ms cubic-bezier(0,0,0.2,1) forwards`
  : 'none';

  return (
    <>
      <style>{`
        @keyframes clawFlipOut {
          from { transform: perspective(900px) rotateY(0deg);  opacity: 1; }
          to   { transform: perspective(900px) rotateY(-88deg); opacity: 0; }
        }
        @keyframes clawFlipIn {
          from { transform: perspective(900px) rotateY(88deg); opacity: 0; }
          to   { transform: perspective(900px) rotateY(0deg);  opacity: 1; }
        }
        @keyframes clawProgress {
          from { transform: scaleX(0); }
          to   { transform: scaleX(1); }
        }
      `}</style>

      <div
        className="relative bg-card/80 backdrop-blur-sm rounded-3xl border border-border shadow-lg overflow-hidden select-none"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Decorative background glow */}
        <div
          className="absolute top-0 right-0 w-64 h-64 pointer-events-none"
          style={{
            background: 'radial-gradient(circle, var(--color-primary), transparent 65%)',
            opacity: 0.06,
            transform: 'translate(30%, -30%)',
          }}
        />

        {/* Flipping content */}
        <div className="p-8 pb-4" style={{ animation: flipAnim }}>

          {/* Tag */}
          <div className="inline-flex items-center gap-2 mb-7">
            <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
            <span
              className="text-primary font-caption font-bold"
              style={{ fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase' }}
            >
              {slide.tag}
            </span>
          </div>

          {/* Icon */}
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 shadow-md flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, var(--color-primary), var(--color-secondary))' }}
          >
            <Icon name={slide.icon} size={26} color="white" strokeWidth={1.75} />
          </div>

          {/* Headline */}
          <h2
            className="font-heading font-bold text-foreground leading-[1.18] mb-3"
            style={{ fontSize: 'clamp(22px, 2.2vw, 27px)' }}
          >
            {slide.headline.split('\n').map((line, i) => (
              <React.Fragment key={i}>{i > 0 && <br />}{line}</React.Fragment>
            ))}
          </h2>

          {/* Body */}
          <p className="text-sm text-muted-foreground font-caption leading-relaxed">
            {slide.body}
          </p>
        </div>

        {/* Footer: prev/next + dots + progress bar */}
        <div className="px-8 pt-2 pb-7">
          <div className="flex items-center gap-3 mb-3">
            {/* Prev */}
            <button
              onClick={goPrev}
              disabled={flipPhase !== 'idle'}
              className="w-6 h-6 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 transition-colors flex-shrink-0"
            >
              <Icon name="ChevronLeft" size={13} />
            </button>

            {/* Dots */}
            <div className="flex items-center gap-2 flex-1">
              {BENEFIT_SLIDES.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  disabled={flipPhase !== 'idle'}
                  className="rounded-full transition-all duration-300 disabled:cursor-default"
                  style={{
                    width:      i === displayedIdx ? 20 : 6,
                    height:     6,
                    background: i === displayedIdx ? 'var(--color-primary)' : 'var(--color-border)',
                  }}
                />
              ))}
            </div>

            {/* Next */}
            <button
              onClick={goNext}
              disabled={flipPhase !== 'idle'}
              className="w-6 h-6 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 transition-colors flex-shrink-0"
            >
              <Icon name="ChevronRight" size={13} />
            </button>
          </div>

          {/* Progress bar */}
          <div className="h-px bg-border rounded-full overflow-hidden">
            <div
              key={displayedIdx}
              style={{
                height: '100%',
                background: 'linear-gradient(90deg, var(--color-primary), var(--color-secondary))',
                transformOrigin: 'left center',
                animationName:          flipPhase === 'idle' ? 'clawProgress' : 'none',
                animationDuration:      `${SHOW_MS}ms`,
                animationTimingFunction: 'linear',
                animationFillMode:      'forwards',
                animationPlayState:     isPaused ? 'paused' : 'running',
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_PATTERN = /^\+?[0-9()\-\s]{7,}$/;
const PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d\s]).{8,}$/;

const validateSignupData = (data) => {
  const errors = {};

  if (!data.firstName.trim()) errors.firstName = 'First name is required.';
  if (!data.lastName.trim()) errors.lastName = 'Last name is required.';
  if (!data.companyName.trim()) errors.companyName = 'Company name is required.';
  if (!data.address.trim()) errors.address = 'Address is required.';

  if (!data.phoneNumber.trim()) {
    errors.phoneNumber = 'Phone number is required.';
  } else if (!PHONE_PATTERN.test(data.phoneNumber.trim())) {
    errors.phoneNumber = 'Enter a valid phone number.';
  }

  if (!data.email.trim()) {
    errors.email = 'Email is required.';
  } else if (!EMAIL_PATTERN.test(data.email.trim())) {
    errors.email = 'Enter a valid email address.';
  }

  if (!data.password) {
    errors.password = 'Password is required.';
  } else if (!PASSWORD_PATTERN.test(data.password)) {
    errors.password = 'Use 8+ characters with uppercase, lowercase, number, and symbol.';
  }

  if (!data.confirmPassword) {
    errors.confirmPassword = 'Please confirm your password.';
  } else if (data.password !== data.confirmPassword) {
    errors.confirmPassword = 'Passwords do not match.';
  }

  return errors;
};

const AdminAuth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, isAuthenticated,forgotPassword } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [signupErrors, setSignupErrors] = useState({});
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);

  // Step: 'form' | 'theme'
  const [signupStep, setSignupStep] = useState('form');
  const [isTransitioning, setIsTransitioning] = useState(false);

  const [loginData, setLoginData] = useState({ email: '', password: '' });

  const [signupData, setSignupData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    companyName: '',
    address: '',
    phoneNumber: '',
    companyDescription: '',
    theme: 'default',
  });

  const updateSignupField = (field, value) => {
    setSignupData((prev) => ({ ...prev, [field]: value }));
    setSignupErrors((prev) => {
      const nextErrors = { ...prev };
      delete nextErrors[field];

      if (field === 'password' || field === 'confirmPassword') {
        delete nextErrors.password;
        delete nextErrors.confirmPassword;
      }

      return nextErrors;
    });
    setError('');
  };

  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/bookings-management';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const result = await login(loginData.email, loginData.password);
      if (!result.success) {
        setError(result.error);
      } else {
        const from = location.state?.from?.pathname || '/bookings-management';
        navigate(from, { replace: true });
      }
    } catch {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignupNext = (e) => {
    e.preventDefault();
    const validationErrors = validateSignupData(signupData);

    if (Object.keys(validationErrors).length > 0) {
      setSignupErrors(validationErrors);
      setError('Please correct the highlighted fields before continuing.');
      return;
    }

    setError('');

    // Transition to theme step
    setIsTransitioning(true);
    setTimeout(() => {
      setSignupStep('theme');
      setIsTransitioning(false);
    }, 300);
  };

  const handleThemeSelect = (themeId) => {
    setSignupData(prev => ({ ...prev, theme: themeId }));
  };

  const handleSignupSubmit = async () => {
    const validationErrors = validateSignupData(signupData);

    if (Object.keys(validationErrors).length > 0) {
      setSignupErrors(validationErrors);
      setSignupStep('form');
      setError('Please correct the highlighted fields before creating your account.');
      return;
    }

    setError('');
    setIsLoading(true);
    try {
      const result = await register({
        email: signupData.email.trim().toLowerCase(),
        password: signupData.password,
        firstName: signupData.firstName.trim(),
        lastName: signupData.lastName.trim(),
        companyName: signupData.companyName.trim(),
        address: signupData.address.trim(),
        phoneNumber: signupData.phoneNumber.trim(),
        companyDescription: signupData.companyDescription.trim(),
        theme: signupData.theme,
      });

      if (!result.success) {
        setError(result.error);
      } else {
        navigate('/bookings-management', { replace: true });
      }
    } catch {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setSignupStep('form');
      setIsTransitioning(false);
    }, 300);
  };

  const handleTabSwitch = (toLogin) => {
    setIsLogin(toLogin);
    setError('');
    setSignupErrors({});
    setShowLoginPassword(false);
    setShowSignupPassword(false);
    setShowConfirmPassword(false);
    setSignupStep('form');
    setIsForgotPassword(false);
    setForgotPasswordSuccess(false);
    setForgotPasswordEmail('');
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    if (!forgotPasswordEmail.trim()) {
      setError('Please enter your email address.');
      return;
    }
    
    setError('');
    setIsLoading(true);
    
    // Call the context method
    const result = await forgotPassword(forgotPasswordEmail.trim());
    
    if (result.success) {
      setForgotPasswordSuccess(true);
    } else {
      setError(result.error);
    }
    
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 flex items-center justify-center p-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-1/2 -left-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-6xl mx-auto relative">
        <div className="grid lg:grid-cols-2 gap-8 items-start">

          {/* Left side — Branding */}
          <div className="hidden lg:block sticky top-8 self-start">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-3 bg-card/80 backdrop-blur-sm rounded-2xl px-6 py-4 shadow-lg border border-border">
                <AppLogo size={32} shape="rounded" bg="brand" />
                <div>
                  <h1 className="font-heading font-bold text-3xl text-foreground">Claw</h1>
                  <p className="text-sm text-muted-foreground font-caption">Business Management</p>
                </div>
              </div>

              <BenefitCard />
            </div>
          </div>

          {/* Right side — Auth Forms */}
          <div className="w-full">
            <div className="bg-card/80 backdrop-blur-sm rounded-3xl shadow-2xl p-8 md:p-10 border border-border overflow-hidden">

              {/* Logo for mobile */}
              <div className="lg:hidden mb-8 text-center">
                <div className="inline-flex items-center gap-3 mb-2">
                  <AppLogo size={24} shape="rounded" bg="brand" />
                  <h1 className="font-heading font-bold text-2xl text-foreground">Claw</h1>
                </div>
                <p className="text-sm text-muted-foreground font-caption">Business Management</p>
              </div>

              {/* Tab Switcher */}
              <div className="flex gap-2 mb-8 bg-muted/50 p-1 rounded-xl">
                <button
                  onClick={() => handleTabSwitch(true)}
                  className={`flex-1 py-3 rounded-lg font-heading font-semibold transition-all duration-200 ${
                    isLogin ? 'bg-card text-primary shadow-md' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Sign In
                </button>
                <button
                  onClick={() => handleTabSwitch(false)}
                  className={`flex-1 py-3 rounded-lg font-heading font-semibold transition-all duration-200 ${
                    !isLogin ? 'bg-card text-primary shadow-md' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Sign Up
                </button>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-xl flex items-start gap-3">
                  <Icon name="AlertCircle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-error font-caption">{error}</p>
                </div>
              )}

              {/* ── LOGIN FORM ── */}
              {isLogin ? (
                isForgotPassword ? (
                  /* ── FORGOT PASSWORD VIEW ── */
                  <div className="space-y-5 animate-in fade-in slide-in-from-right-4 duration-300">
                    <div className="text-center mb-6">
                      <h3 className="font-heading font-bold text-xl text-foreground mb-2">Reset Password</h3>
                      <p className="text-sm text-muted-foreground font-caption">
                        Enter your email address and we'll send you a link to reset your password.
                      </p>
                    </div>

                    {forgotPasswordSuccess ? (
                      <div className="p-6 bg-primary/10 border border-primary/20 rounded-xl text-center space-y-4">
                        <div className="mx-auto w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mb-2">
                          <Icon name="Mail" size={24} color="var(--color-primary)" />
                        </div>
                        <h4 className="font-heading font-semibold text-foreground">Check your inbox</h4>
                        <p className="text-sm text-muted-foreground font-caption">
                          We've sent a password reset link to <strong>{forgotPasswordEmail}</strong>.
                        </p>
                        <Button 
                          type="button" 
                          variant="outline" 
                          className="w-full mt-4"
                          onClick={() => handleTabSwitch(true)}
                        >
                          Back to Sign In
                        </Button>
                      </div>
                    ) : (
                      <form onSubmit={handleForgotPasswordSubmit} className="space-y-5">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2 font-heading">
                            Email Address
                          </label>
                          <div className="relative">
                            <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10">
                              <Icon name="Mail" size={18} color="var(--color-muted-foreground)" />
                            </div>
                            <Input
                              type="email"
                              placeholder="your@email.com"
                              value={forgotPasswordEmail}
                              onChange={(e) => setForgotPasswordEmail(e.target.value)}
                              required
                              className="pl-10 placeholder:text-muted-foreground/60"
                            />
                          </div>
                        </div>

                        <div className="space-y-3">
                          <Button type="submit" variant="default" className="w-full" disabled={isLoading}>
                            {isLoading ? (
                              <><Icon name="Loader" size={18} className="animate-spin mr-2" />Sending...</>
                            ) : (
                              <><Icon name="Send" size={18} className="mr-2" />Send Reset Link</>
                            )}
                          </Button>
                          <Button 
                            type="button" 
                            variant="ghost" 
                            className="w-full text-muted-foreground hover:text-foreground"
                            onClick={() => {
                              setIsForgotPassword(false);
                              setError('');
                            }}
                            disabled={isLoading}
                          >
                            <Icon name="ArrowLeft" size={16} className="mr-2" />
                            Back to Sign In
                          </Button>
                        </div>
                      </form>
                    )}
                  </div>
                ) : (
                  /* ── EXISTING LOGIN FORM ── */
                  <form onSubmit={handleLoginSubmit} className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2 font-heading">
                        Email Address
                      </label>
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2">
                          <Icon name="Mail" size={18} color="var(--color-muted-foreground)" />
                        </div>
                        <Input
                          type="email"
                          placeholder="your@email.com"
                          value={loginData.email}
                          onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                          required
                          className="pl-10 placeholder:text-muted-foreground/60"
                        />
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-medium text-foreground font-heading">
                          Password
                        </label>
                        <button
                          type="button"
                          onClick={() => {
                            setIsForgotPassword(true);
                            setError('');
                            setForgotPasswordEmail(loginData.email); // Auto-fill if they already typed it
                          }}
                          className="text-xs text-primary hover:underline font-caption focus:outline-none"
                        >
                          Forgot password?
                        </button>
                      </div>
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10">
                          <Icon name="Lock" size={18} color="var(--color-muted-foreground)" />
                        </div>
                        <Input
                          type={showLoginPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          value={loginData.password}
                          onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                          autoComplete="current-password"
                          required
                          className="pl-10 pr-10 placeholder:text-muted-foreground/[0.15]"
                        />
                        <button
                          type="button"
                          onClick={() => setShowLoginPassword((prev) => !prev)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 z-10 text-muted-foreground hover:text-foreground transition-colors"
                          aria-label={showLoginPassword ? 'Hide password' : 'Show password'}
                        >
                          <Icon name={showLoginPassword ? 'EyeOff' : 'Eye'} size={18} />
                        </button>
                      </div>
                    </div>

                    <Button type="submit" variant="default" className="w-full" disabled={isLoading}>
                      {isLoading ? (
                        <><Icon name="Loader" size={18} className="animate-spin mr-2" />Signing in...</>
                      ) : (
                        <><Icon name="LogIn" size={18} className="mr-2" />Sign In</>
                      )}
                    </Button>
                  </form>
                )
              ) : (
                /* ── SIGNUP — with slide transition ── */
                <div
                  className="transition-all duration-300"
                  style={{
                    opacity: isTransitioning ? 0 : 1,
                    transform: isTransitioning ? 'translateX(30px)' : 'translateX(0)',
                  }}
                >
                  {/* ── STEP 1: Signup form ── */}
                  {signupStep === 'form' && (
                    <form onSubmit={handleSignupNext} className="space-y-5" noValidate>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2 font-heading">First Name</label>
                          <Input
                            type="text"
                            placeholder="John"
                            value={signupData.firstName}
                            onChange={(e) => updateSignupField('firstName', e.target.value)}
                            autoComplete="given-name"
                            aria-invalid={Boolean(signupErrors.firstName)}
                            required
                            className={`placeholder:text-muted-foreground/60 ${signupErrors.firstName ? 'border-error focus-visible:ring-error' : ''}`}
                          />
                          {signupErrors.firstName && (
                            <p className="text-xs text-error mt-1 font-caption">{signupErrors.firstName}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2 font-heading">Last Name</label>
                          <Input
                            type="text"
                            placeholder="Doe"
                            value={signupData.lastName}
                            onChange={(e) => updateSignupField('lastName', e.target.value)}
                            autoComplete="family-name"
                            aria-invalid={Boolean(signupErrors.lastName)}
                            required
                            className={`placeholder:text-muted-foreground/60 ${signupErrors.lastName ? 'border-error focus-visible:ring-error' : ''}`}
                          />
                          {signupErrors.lastName && (
                            <p className="text-xs text-error mt-1 font-caption">{signupErrors.lastName}</p>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2 font-heading">Company Name</label>
                        <div className="relative">
                          <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10">
                            <Icon name="Briefcase" size={18} color="var(--color-muted-foreground)" />
                          </div>
                          <Input
                            type="text"
                            placeholder="Your Company Name"
                            value={signupData.companyName}
                            onChange={(e) => updateSignupField('companyName', e.target.value)}
                            autoComplete="organization"
                            aria-invalid={Boolean(signupErrors.companyName)}
                            required
                            className={`pl-10 placeholder:text-muted-foreground/60 ${signupErrors.companyName ? 'border-error focus-visible:ring-error' : ''}`}
                          />
                        </div>
                        {signupErrors.companyName && (
                          <p className="text-xs text-error mt-1 font-caption">{signupErrors.companyName}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2 font-heading">Address</label>
                        <div className="relative">
                          <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10">
                            <Icon name="MapPin" size={18} color="var(--color-muted-foreground)" />
                          </div>
                          <Input
                            type="text"
                            placeholder="123 Main St, City"
                            value={signupData.address}
                            onChange={(e) => updateSignupField('address', e.target.value)}
                            autoComplete="street-address"
                            aria-invalid={Boolean(signupErrors.address)}
                            required
                            className={`pl-10 placeholder:text-muted-foreground/60 ${signupErrors.address ? 'border-error focus-visible:ring-error' : ''}`}
                          />
                        </div>
                        {signupErrors.address && (
                          <p className="text-xs text-error mt-1 font-caption">{signupErrors.address}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2 font-heading">Phone Number</label>
                        <div className="relative">
                          <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10">
                            <Icon name="Phone" size={18} color="var(--color-muted-foreground)" />
                          </div>
                          <Input
                            type="tel"
                            placeholder="+1234567890"
                            value={signupData.phoneNumber}
                            onChange={(e) => updateSignupField('phoneNumber', e.target.value)}
                            autoComplete="tel"
                            aria-invalid={Boolean(signupErrors.phoneNumber)}
                            required
                            className={`pl-10 placeholder:text-muted-foreground/60 ${signupErrors.phoneNumber ? 'border-error focus-visible:ring-error' : ''}`}
                          />
                        </div>
                        {signupErrors.phoneNumber && (
                          <p className="text-xs text-error mt-1 font-caption">{signupErrors.phoneNumber}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2 font-heading">Email Address</label>
                        <div className="relative">
                          <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10">
                            <Icon name="Mail" size={18} color="var(--color-muted-foreground)" />
                          </div>
                          <Input
                            type="email"
                            placeholder="your@email.com"
                            value={signupData.email}
                            onChange={(e) => updateSignupField('email', e.target.value)}
                            autoComplete="email"
                            aria-invalid={Boolean(signupErrors.email)}
                            required
                            className={`pl-10 placeholder:text-muted-foreground/60 ${signupErrors.email ? 'border-error focus-visible:ring-error' : ''}`}
                          />
                        </div>
                        {signupErrors.email && (
                          <p className="text-xs text-error mt-1 font-caption">{signupErrors.email}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2 font-heading">Password</label>
                        <div className="relative">
                          <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10">
                            <Icon name="Lock" size={18} color="var(--color-muted-foreground)" />
                          </div>
                          <Input
                            type={showSignupPassword ? 'text' : 'password'}
                            placeholder="••••••••"
                            value={signupData.password}
                            onChange={(e) => updateSignupField('password', e.target.value)}
                            autoComplete="new-password"
                            aria-invalid={Boolean(signupErrors.password)}
                            required
                            className={`pl-10 pr-10 placeholder:text-muted-foreground/[0.15] ${signupErrors.password ? 'border-error focus-visible:ring-error' : ''}`}
                          />
                          <button
                            type="button"
                            onClick={() => setShowSignupPassword((prev) => !prev)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 z-10 text-muted-foreground hover:text-foreground transition-colors"
                            aria-label={showSignupPassword ? 'Hide password' : 'Show password'}
                          >
                            <Icon name={showSignupPassword ? 'EyeOff' : 'Eye'} size={18} />
                          </button>
                        </div>
                        <p className={`text-xs mt-1 font-caption ${signupErrors.password ? 'text-error' : 'text-muted-foreground'}`}>
                          {signupErrors.password || 'Use 8+ characters with uppercase, lowercase, number, and symbol.'}
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2 font-heading">Confirm Password</label>
                        <div className="relative">
                          <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10">
                            <Icon name="Lock" size={18} color="var(--color-muted-foreground)" />
                          </div>
                          <Input
                            type={showConfirmPassword ? 'text' : 'password'}
                            placeholder="••••••••"
                            value={signupData.confirmPassword}
                            onChange={(e) => updateSignupField('confirmPassword', e.target.value)}
                            autoComplete="new-password"
                            aria-invalid={Boolean(signupErrors.confirmPassword)}
                            required
                            className={`pl-10 pr-10 placeholder:text-muted-foreground/[0.15] ${signupErrors.confirmPassword ? 'border-error focus-visible:ring-error' : ''}`}
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword((prev) => !prev)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 z-10 text-muted-foreground hover:text-foreground transition-colors"
                            aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                          >
                            <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={18} />
                          </button>
                        </div>
                        {signupErrors.confirmPassword && (
                          <p className="text-xs text-error mt-1 font-caption">{signupErrors.confirmPassword}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2 font-heading">
                          Company Description <span className="text-muted-foreground">(Optional)</span>
                        </label>
                        <textarea
                          placeholder="Tell us about your business..."
                          value={signupData.companyDescription}
                          onChange={(e) => updateSignupField('companyDescription', e.target.value)}
                          className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground ring-offset-background placeholder:text-muted-foreground/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 font-caption"
                          rows={3}
                        />
                      </div>

                      <Button type="submit" variant="default" className="w-full">
                        <Icon name="ArrowRight" size={18} className="mr-2" />
                        Next — Choose Your Theme
                      </Button>
                    </form>
                  )}

                  {/* ── STEP 2: Theme picker ── */}
                  {signupStep === 'theme' && (
                    <div className="space-y-6">
                      {/* Header */}
                      <div>
                        <button
                          onClick={handleBack}
                          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4 font-caption"
                        >
                          <Icon name="ArrowLeft" size={16} />
                          Back to details
                        </button>
                        <h2 className="font-heading font-bold text-xl text-foreground">
                          Choose Your Theme
                        </h2>
                        <p className="text-sm text-muted-foreground font-caption mt-1">
                          Pick the look that best represents your brand. You can change this anytime.
                        </p>
                      </div>

                      {/* Theme grid */}
                      <div className="grid grid-cols-3 gap-3 max-h-80 overflow-y-auto pt-1 px-1 pr-1 custom-scrollbar">
                        {THEMES.map((theme) => {
                          const isSelected = signupData.theme === theme.id;
                          return (
                            <button
                              key={theme.id}
                              type="button"
                              onClick={() => handleThemeSelect(theme.id)}
                              className="group flex flex-col gap-2 text-left focus:outline-none"
                            >
                              {/* Preview card */}
                              <div
                                className="relative rounded-xl overflow-hidden transition-all duration-200"
                                style={{
                                  outline: isSelected
                                    ? `2px solid var(--color-primary)`
                                    : '2px solid transparent',
                                  boxShadow: isSelected
                                    ? '0 0 0 4px var(--color-primary)20'
                                    : 'none',
                                  transform: isSelected ? 'scale(1.03)' : 'scale(1)',
                                }}
                              >
                                <ThemePreview theme={theme} />

                                {/* Selected checkmark */}
                                {isSelected && (
                                  <div
                                    className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center"
                                    style={{ background: theme.primary }}
                                  >
                                    <Icon name="Check" size={11} color="white" />
                                  </div>
                                )}

                                {/* Tag badge */}
                                <div
                                  className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-center font-caption"
                                  style={{
                                    background: theme.primary,
                                    color: '#fff',
                                    fontSize: 8,
                                    fontWeight: 600,
                                    letterSpacing: '0.04em',
                                    textTransform: 'uppercase',
                                  }}
                                >
                                  {theme.tag}
                                </div>
                              </div>

                              {/* Theme name */}
                              <div className="px-0.5">
                                <p className="text-xs font-heading font-semibold text-foreground leading-tight truncate">
                                  {theme.name}
                                </p>
                                <p className="text-xs text-muted-foreground font-caption leading-tight truncate">
                                  {theme.description}
                                </p>
                              </div>
                            </button>
                          );
                        })}
                      </div>

                      {/* Selected theme confirmation */}
                      {signupData.theme && (
                        <div className="flex items-center gap-3 p-3 bg-primary/5 border border-primary/15 rounded-xl">
                          <div
                            className="w-8 h-8 rounded-lg flex-shrink-0"
                            style={{
                              background: `linear-gradient(135deg, ${THEMES.find(t => t.id === signupData.theme)?.primary}, ${THEMES.find(t => t.id === signupData.theme)?.secondary})`,
                            }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-heading font-semibold text-foreground">
                              {THEMES.find(t => t.id === signupData.theme)?.name}
                            </p>
                            <p className="text-xs text-muted-foreground font-caption">
                              {THEMES.find(t => t.id === signupData.theme)?.description}
                            </p>
                          </div>
                          <Icon name="CheckCircle" size={18} color="var(--color-primary)" />
                        </div>
                      )}

                      {/* Error in theme step */}
                      {error && (
                        <div className="p-4 bg-error/10 border border-error/20 rounded-xl flex items-start gap-3">
                          <Icon name="AlertCircle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-error font-caption">{error}</p>
                        </div>
                      )}

                      <Button
                        variant="default"
                        className="w-full"
                        disabled={isLoading || !signupData.theme}
                        onClick={handleSignupSubmit}
                      >
                        {isLoading ? (
                          <><Icon name="Loader" size={18} className="animate-spin mr-2" />Creating account...</>
                        ) : (
                          <><Icon name="UserPlus" size={18} className="mr-2" />Create Account</>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Footer */}
              <div className="mt-6 text-center">
                <p className="text-xs text-muted-foreground font-caption">
                  By continuing, you agree to our Terms of Service and Privacy Policy
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAuth;
